import { Link } from 'react-router-dom';
import { btns } from '../components/buttons';
import { UserText } from '../components/UserText';
import { useCanWrite } from '../lib/useCanWrite';
import { useOnboardingRequestReminders } from './useOnboardingRequests';

const cls = {
  panel:   'bg-surface border border-line border-l-[3px] border-l-bronze rounded-token p-5 shadow-panel mb-10',
  heading: 'font-serif text-[15px] font-normal m-0 mb-2 text-navy',
  body:    'text-[13px] text-body font-sans m-0 max-w-2xl',
  actions: 'mt-4 flex flex-wrap items-center gap-3',
  link:    `${btns.btnPrim} no-underline inline-block`,
};

/**
 * The Dashboard's standing reminder that a firm has asked for the family's details
 * (steward-app#1427).
 *
 * Modelled on the Dashboard's "Needs attention" list rather than on `NameNudge`: it is derived
 * from data already fetched for the People nav dot (`useOnboardingRequestsQuery`, one shared
 * cache entry), costs no request of its own, and shows for exactly as long as a request is open.
 * It offers **"Remind me later"** and never a dismiss, because the request is another person's
 * and does not stop being true because someone closed a card. "Later" is three days, or sooner
 * if the firm asks again (`isOnboardingRequestSnoozed`).
 *
 * Names who asked (the firm name from the family's own connection record, via the façade) and
 * what for. Tax identification is not asked for, so the copy never names it, the same rule as
 * the notice on People.
 *
 * Renders nothing unless the read succeeded, for the same reasons the notice gives.
 */
export function OnboardingRequestCard() {
  const { visible, remindLater } = useOnboardingRequestReminders();
  const canWrite = useCanWrite();
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map(request => (
        <section key={request.firmId} className={cls.panel} aria-label={`Request from ${request.firmName}`}>
          <h2 className={cls.heading}>
            <UserText>{request.firmName}</UserText> has asked for your family's details
          </h2>
          <p className={cls.body}>
            They would like a legal name, a date of birth, and a legal address for each person in
            your family.{' '}
            {canWrite
              ? 'When everything is in, you can let them know from the People page.'
              : 'Someone in your family who can edit these records can send them from the People page.'}
          </p>
          <div className={cls.actions}>
            <Link to="/people" className={cls.link}>Review your family</Link>
            <button type="button" className={btns.btn} onClick={() => remindLater(request)}>
              Remind me later
            </button>
          </div>
        </section>
      ))}
    </>
  );
}
